import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Smartphone, X } from 'lucide-react';
import { isPWAInstalled, showInstallPrompt } from '@/utils/registerSW';
import { isMobileApp } from '@/utils/mobileBridge';

export default function InstallPWAButton() {
  const { t } = useTranslation();
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isMobileApp()) return;
    setIsInstalled(isPWAInstalled());

    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua);
    setIsIOS(iOS);

    // Browser šalje beforeinstallprompt na sve listenere; setupInstallPrompt()
    // u main.tsx čuva event u modul-level varijabli za showInstallPrompt().
    // Ovaj listener samo otkriva da je install dostupan (UI state).
    const handleBeforeInstall = () => setCanInstall(true);
    const handleInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  // Apple App Store Review Guideline 4.2 (Minimum Functionality):
  // ne reklamiraj install/PWA CTA unutar native WebView wrapper-a.
  // isMobileApp() detektuje react-native-webview bridge handshake.
  if (isMobileApp()) return null;
  if (isInstalled) return null;
  if (!isIOS && !canInstall) return null;

  const handleClick = async () => {
    if (isIOS) {
      setShowIOSHint(true);
      return;
    }
    if (canInstall) {
      const accepted = await showInstallPrompt();
      if (accepted) setIsInstalled(true);
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="group inline-flex items-center justify-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 text-white font-semibold transition hover:bg-white/20 hover:border-white/40 active:scale-95"
        aria-label={t('hero.installApp')}
      >
        <Download size={18} />
        <span className="text-sm">{t('hero.installApp')}</span>
      </button>

      {showIOSHint && isIOS && (
        <div
          onClick={() => setShowIOSHint(false)}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 rounded-3xl p-6 max-w-md w-full border border-white/10 relative"
          >
            <button
              onClick={() => setShowIOSHint(false)}
              aria-label={t('common.close')}
              className="absolute top-4 right-4 text-white/60 hover:text-white"
            >
              <X size={22} />
            </button>
            <div className="flex items-center gap-3 mb-4 pr-8">
              <Smartphone size={24} className="text-purple-400" />
              <h3 className="text-white text-xl font-bold">
                {t('hero.iosInstallTitle')}
              </h3>
            </div>
            <ol className="text-white/80 space-y-3 mb-6">
              {[1, 2, 3].map((i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-600 text-white text-sm font-bold flex items-center justify-center">
                    {i}
                  </span>
                  <span>{t(`hero.iosStep${i}`)}</span>
                </li>
              ))}
            </ol>
            <button
              onClick={() => setShowIOSHint(false)}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 rounded-2xl transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
